import { maxBy, minBy } from 'es-toolkit';
import { createSelector } from 'reselect';

import { Component, Member, RelativeMemberGeometry } from '@paragon/contract-library';
import { Direction2D, Line2D, LineSegment2D, Point2D, Polygon2D } from '@paragon/geometry-library';
import { notNullish } from '@paragon/utilities';

import { calculateLineLocationOfMemberOrNonContinuousBearingGeometry, findAndCalculateLine } from './lines';

function areArraysShallowlyEqual(prev: unknown[], next: unknown[]): boolean {
  if (prev.length !== next.length) {
    return false;
  }

  for (let i = 0; i < prev.length; i++) {
    if (prev[i] !== next[i]) {
      return false;
    }
  }

  return true;
}

export const findAndCalculateBaseCuts = createSelector(
  (memberGeometry: RelativeMemberGeometry) => memberGeometry,
  (memberGeometry: RelativeMemberGeometry, component: Component) => component,
  (memberGeometry, component) =>
    memberGeometry.baseCutGuids.map((lineGuid) => findAndCalculateLine(lineGuid, component)).filter(notNullish),
  {
    memoizeOptions: {
      resultEqualityCheck: areArraysShallowlyEqual,
    },
  },
);

export const findAndCalculateEndCuts = createSelector(
  (memberGeometry: RelativeMemberGeometry) => memberGeometry,
  (memberGeometry: RelativeMemberGeometry, component: Component) => component,
  (memberGeometry, component) =>
    memberGeometry.endCutGuids.map((lineGuid) => findAndCalculateLine(lineGuid, component)).filter(notNullish),
  {
    memoizeOptions: {
      resultEqualityCheck: areArraysShallowlyEqual,
    },
  },
);

export function calculateMemberGeometry(member: Member, component: Component) {
  if (member.relativeGeometry) {
    const relativeGeometry = calculateRelativeMemberGeometry(member.relativeGeometry, component);
    if (!relativeGeometry) return null;
    return relativeGeometry.geometry;
  }

  return member.geometry;
}

export const calculateRelativeMemberGeometry = createSelector(
  (memberGeometry: RelativeMemberGeometry) => memberGeometry,
  (memberGeometry: RelativeMemberGeometry, component: Component) =>
    findAndCalculateLine(memberGeometry.lineGuid, component),
  findAndCalculateBaseCuts,
  findAndCalculateEndCuts,
  (memberGeometry, line, passedBaseCuts, passedEndCuts) => {
    // This code only works for convex geometries.
    const upperLine = calculateLineLocationOfMemberOrNonContinuousBearingGeometry(memberGeometry, line, 'Port');
    const lowerLine = calculateLineLocationOfMemberOrNonContinuousBearingGeometry(memberGeometry, line, 'Starboard');
    if (!upperLine || !lowerLine) return null;
    const baseCuts = passedBaseCuts.filter(
      (cut) => cut.intersectWithLine(upperLine) !== null && cut.intersectWithLine(lowerLine) !== null,
    );
    const endCuts = passedEndCuts.filter(
      (cut) => cut.intersectWithLine(upperLine) !== null && cut.intersectWithLine(lowerLine) !== null,
    );

    if (baseCuts.length === 0 || endCuts.length === 0) return null;
    const point1 = upperLine.intersectWithLine(baseCuts[0])!;
    const point2 = upperLine.intersectWithLine(endCuts[0])!;
    const point3 = lowerLine.intersectWithLine(endCuts[0])!;
    const point4 = lowerLine.intersectWithLine(baseCuts[0])!;
    console.assert(upperLine.isParallelTo(lowerLine));
    const midline = new Line2D(scaleVector(upperLine.basePoint.add(lowerLine.basePoint), 0.5), upperLine.direction);
    const vectorTowardEnd = point2.add(point3).subtract(point1).subtract(point4);
    if (vectorTowardEnd.equals(Point2D.origin)) {
      return null;
    }
    const midlineBasePoint = maxBy(
      baseCuts.map((cut) => cut.intersectWithLine(midline)).filter((point) => point),
      (point) => getDotProduct(point!, vectorTowardEnd),
    );
    const midlineEndPoint = minBy(
      endCuts.map((cut) => cut.intersectWithLine(midline)).filter((point) => point),
      (point) => getDotProduct(point!, vectorTowardEnd),
    );
    if (
      !midlineBasePoint ||
      !midlineEndPoint ||
      midlineBasePoint.equals(midlineEndPoint) ||
      getDotProduct(midlineEndPoint.subtract(midlineBasePoint), vectorTowardEnd) <= 0
    ) {
      return null;
    }
    const midlineSegment = new LineSegment2D(midlineBasePoint, midlineEndPoint);
    const upperLinePointsOffset = getDotProduct(point2.subtract(point1), vectorTowardEnd);
    const lowerLinePointsOffset = getDotProduct(point3.subtract(point4), vectorTowardEnd);
    let geometry: readonly Point2D[] | null =
      upperLinePointsOffset < 0
        ? [baseCuts[0].intersectWithLine(endCuts[0])!, point3, point4]
        : lowerLinePointsOffset < 0
          ? [point1, point2, baseCuts[0].intersectWithLine(endCuts[0])!]
          : [point1, point2, point3, point4];
    baseCuts.splice(0, 1);
    endCuts.splice(0, 1);
    geometry = removeDuplicatePointsFromPolygon(geometry);
    for (const cut of baseCuts) {
      const pointInDesiredHalfplane = cut.intersectWithLine(midline)!.add(vectorTowardEnd);
      if (cut.containsPoint(pointInDesiredHalfplane)) {
        return null;
      }
      geometry = slicePolygon(geometry, cut, pointInDesiredHalfplane);
    }
    for (const cut of endCuts) {
      const pointInDesiredHalfplane = cut.intersectWithLine(midline)!.subtract(vectorTowardEnd);
      if (cut.containsPoint(pointInDesiredHalfplane)) {
        return null;
      }
      geometry = slicePolygon(geometry, cut, pointInDesiredHalfplane);
    }

    return geometry && geometry.length >= 3 ? { geometry, midline: midlineSegment } : null;
  },
);

function scaleVector(vector: Point2D | Direction2D, scalar: number) {
  return new Point2D(vector.x * scalar, vector.y * scalar);
}

function getDotProduct(vector0: Point2D | Direction2D, vector1: Point2D | Direction2D) {
  return vector0.x * vector1.x + vector0.y * vector1.y;
}

// presumes points are ordered around hull, and polygon is closed
export function removeDuplicatePointsFromPolygon(points: readonly Point2D[] | null) {
  if (!points) {
    return points;
  }
  const result = [];
  let previousPoint: undefined | Point2D;
  for (const point of points) {
    if (!previousPoint) previousPoint = points[points.length - 1];
    if (!point.equals(previousPoint)) {
      result.push(point);
    }
    previousPoint = point;
  }
  return result;
}

// not meant for general use—assumes polygon is convex.
function slicePolygon(polygon: readonly Point2D[] | null, cutLine: Line2D, pointInDesiredHalfplane: Point2D) {
  if (!polygon) return null;

  const halfplaneDirection = getVectorRejection(pointInDesiredHalfplane.subtract(cutLine.basePoint), cutLine.direction);
  const threshold = getDotProduct(cutLine.basePoint, halfplaneDirection);
  const halfplaneChecker = (point: Point2D) => getDotProduct(point, halfplaneDirection) - threshold;

  return (
    new Polygon2D(polygon).slice(cutLine).filter(({ centroid }) => halfplaneChecker(centroid) > 0)[0]?.vertices ?? null
  );
}

// Decompose vector into a sum of two components, one of which is parallel to perpendicularDirection and one of which
// is perpendicular to it. Return the perpendicular component.
function getVectorRejection(vector: Point2D, perpendicularDirection: Direction2D) {
  const dotProduct = getDotProduct(vector, perpendicularDirection);
  return vector.subtract(scaleVector(perpendicularDirection, dotProduct));
}
